import { useLocation } from 'react-router-dom'
import { EventType, Origin, UseConfirmationActionsOrderDocument, UseConfirmationActionsOrderQuery } from '../graphql'

import getPhoenixUrlParams from '../lib/get-phoenix-url-params'
import Venue from '../typings/Venue'
import useOrder from './useOrder'

interface OrderData {
  venue: Venue
  hasBooking: boolean
  eventType?: EventType
  userRecordingId?: string
  invoiceDownloadLink?: string | null
  voucherCode?: string
}

const getBackToWebsiteLink = ({
  venue,
  hasBooking,
  eventType,
  userRecordingId,
  origin,
}: OrderData & { origin: Origin }) => {
  if ([Origin.ORIGIN_WIDGET, Origin.ORIGIN_PROVIDER_API].includes(origin)) {
    return `/widget/w/${venue.shortId}`
  }

  if (userRecordingId) {
    return `/s/${venue.slug}/videos`
  }

  if (hasBooking && eventType) {
    switch (eventType) {
      case EventType.CLASS:
        return `/scl/${venue.slug}`
      case EventType.COURSE:
        return `/sk/${venue.slug}`
      case EventType.WORKSHOP:
        return `/sw/${venue.slug}`
    }
  }

  return `/sp/${venue.slug}`
}

const getUserProfileLink = ({ venue, origin, hasBooking, userRecordingId }: OrderData & { origin: Origin }) => {
  if ([Origin.ORIGIN_WIDGET, Origin.ORIGIN_PROVIDER_API].includes(origin)) {
    return `/widget/w/${venue.shortId}`
  }

  if (userRecordingId) {
    return `/u/videos`
  }

  if (hasBooking) {
    return '/u'
  }

  return `/u/credits`
}

interface Result {
  goBackToWebsite: string
  goToMyProfile: string
  goToMyBookings: string
  goToMyRecording: string | null
}

const useConfirmationActions = (): Result => {
  const { orderData, isOrderLoading } = useOrder<UseConfirmationActionsOrderQuery>(
    UseConfirmationActionsOrderDocument,
    { nextFetchPolicy: 'standby' },
  )

  const location = useLocation()
  const { origin } = getPhoenixUrlParams(location.search)

  if (!orderData || isOrderLoading) {
    return { goToMyProfile: '/u', goToMyBookings: '/u', goBackToWebsite: '/', goToMyRecording: null }
  }

  const hasBooking = orderData.order.items.some((item) => item.__typename === 'BookingOrderItem')
  const eventBooking = orderData.order.items.find((item) => item.__typename === 'BookingOrderItem')
  const eventType =
    (eventBooking &&
      eventBooking.__typename === 'BookingOrderItem' &&
      eventBooking.booking.__typename === 'EventBooking' &&
      eventBooking.booking.bookableItem.eventType) ||
    undefined
  const userRecordingOrderItem = orderData.order.items.find(
    (item) => item.__typename === 'AssignedProductOrderItem' && item.assignedProduct.__typename === 'AssignedRecording',
  )
  const userRecording =
    userRecordingOrderItem &&
    userRecordingOrderItem.__typename === 'AssignedProductOrderItem' &&
    userRecordingOrderItem.assignedProduct.__typename === 'AssignedRecording'
      ? userRecordingOrderItem.assignedProduct
      : undefined

  const venue = orderData.order.venue

  const goToMyBookings = [Origin.ORIGIN_WIDGET, Origin.ORIGIN_PROVIDER_API].includes(origin)
    ? `/widget/w/${venue.shortId}`
    : `/u`
  const goToMyProfile = getUserProfileLink({ venue, hasBooking, userRecordingId: userRecording?.id, origin })
  const goBackToWebsite = getBackToWebsiteLink({
    venue,
    hasBooking,
    eventType,
    userRecordingId: userRecording?.id,
    origin,
  })
  const goToMyRecording = userRecording
    ? [Origin.ORIGIN_WIDGET, Origin.ORIGIN_PROVIDER_API].includes(origin)
      ? `/widget/w/${venue.shortId}?userrecording=${userRecording.id}`
      : `/videos/${userRecording?.product.id}`
    : null

  return { goToMyBookings, goToMyProfile, goToMyRecording, goBackToWebsite }
}

export default useConfirmationActions
